import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Trophy, PenLine, Wand2, CalendarHeart } from "lucide-react";

export const HolidayBanner = () => {
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  useEffect(() => { api.get("/events/current").then((r) => setEv(r.data)).catch(() => {}); }, []);
  if (!ev) return null;
  const cid = ev.contest?.contest_id;
  const promptQ = `prompt=${encodeURIComponent(ev.weekly_prompt)}${cid ? `&contest=${cid}` : ""}`;

  return (
    <section data-testid="holiday-banner" className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-6">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 p-6 md:p-10"
           style={{ background: `linear-gradient(120deg, ${ev.from}33, #0a0514 45%, ${ev.to}33)` }}>
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full blur-3xl opacity-40" style={{ background: ev.to }} />
        <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full blur-3xl opacity-30" style={{ background: ev.from }} />
        <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-6 lg:gap-10 items-center">
          <div>
            <div data-testid="holiday-banner-eyebrow" className="inline-flex items-center gap-2 rounded-full bg-black/40 border border-white/15 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-white/90 mb-4">
              <CalendarHeart className="h-3.5 w-3.5" style={{ color: ev.to }} /> {ev.holiday} event · {ev.month}
            </div>
            <h2 data-testid="holiday-banner-headline" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-none" style={{ textShadow: `0 0 28px ${ev.from}99` }}>{ev.headline}</h2>
            <p className="mt-3 text-slate-200 max-w-xl">{ev.sub}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {!user && (
                <Link to="/login?mode=signup" data-testid="holiday-welcome-bonus" className="inline-flex items-center gap-2 h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold px-5 text-sm transition-colors">
                  <Gift className="h-4 w-4" /> Join free · +{ev.welcome_bonus} bonus gems
                </Link>
              )}
              <Link to="/contest" data-testid="holiday-contest-link" className="inline-flex items-center gap-2 h-10 rounded-full border border-white/20 bg-black/30 hover:bg-black/50 px-5 text-sm text-white transition-colors">
                <Trophy className="h-4 w-4 text-amber-300" /> {ev.name} contest · winner gets {ev.winner_prize.toLocaleString()} gems
              </Link>
            </div>
          </div>
          <div data-testid="weekly-prompt" className="rounded-2xl bg-black/45 backdrop-blur-md border border-white/10 p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: ev.to }}>Week {ev.week} writing prompt</div>
            <p data-testid="weekly-prompt-text" className="font-serif text-xl md:text-2xl font-semibold leading-snug">“{ev.weekly_prompt}”</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={`/create?${promptQ}`} data-testid="weekly-prompt-ai" className="inline-flex items-center gap-1.5 h-9 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold px-4 text-xs transition-colors"><Wand2 className="h-3.5 w-3.5" /> Write it with AI</Link>
              <Link to={`/write${cid ? `?contest=${cid}` : ""}`} data-testid="weekly-prompt-write" className="inline-flex items-center gap-1.5 h-9 rounded-full border border-white/20 hover:bg-white/5 px-4 text-xs text-white transition-colors"><PenLine className="h-3.5 w-3.5" /> Write it yourself</Link>
            </div>
            <div className="mt-3 text-[11px] text-slate-400">Answers count as entries in this month's contest · {ev.contest?.entries || 0} so far</div>
          </div>
        </div>
      </div>
    </section>
  );
};
